import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import * as XLSX from 'xlsx';
import { normalizeImagePath, fetchStaffImageBlob } from '../../utils/mediaUtils';
import { getImageURL } from '../../utils/api';

export default function LectureHours() {
    // Media service configuration (optional external media service).
    // For this React SPA we primarily rely on the backend /api/staff image route,
    // but still allow overriding via a global pageProps-style object if present.
    const pageProps = typeof window !== 'undefined' ? (window.pageProps || {}) : {};
    const mediaBaseUrl = pageProps?.media?.baseUrl || null;
    const headerName = 'x-api-key';
    const headerValue = pageProps?.auth?.apiKey || null;
    
    // Get current month's first and last day
    const getCurrentMonthDates = () => {
        const now = new Date();
        // Create dates in local timezone to avoid timezone issues
        const year = now.getFullYear();
        const month = now.getMonth();
        
        // First day of current month
        const firstDay = new Date(year, month, 1);
        // Last day of current month (day 0 of next month)
        const lastDay = new Date(year, month + 1, 0);
        
        // Format as YYYY-MM-DD
        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };
        
        return {
            startDate: formatDate(firstDay),
            endDate: formatDate(lastDay)
        };
    };

    const { startDate: defaultStart, endDate: defaultEnd } = getCurrentMonthDates();

    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [loading, setLoading] = useState(false);
    const [chartLoading, setChartLoading] = useState(false);
    const [lectureData, setLectureData] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [imageCache, setImageCache] = useState({});
    const [loadingImages, setLoadingImages] = useState(new Set());
    const [imageErrors, setImageErrors] = useState(new Set());
    const [showModal, setShowModal] = useState(false);
    const [modalSubject, setModalSubject] = useState(null);
    const [viewMode, setViewMode] = useState('bySubject');
    const [showEditModal, setShowEditModal] = useState(false);
    const [editSessionContext, setEditSessionContext] = useState(null); // { subject, session, staff }
    const [editTime, setEditTime] = useState({ start: '', end: '' });
    const [editDurationMin, setEditDurationMin] = useState(null);
    const [savingEdit, setSavingEdit] = useState(false);

    const normalizeSessionDateKey = (dateValue) => {
        if (!dateValue) return '';
        const parsedDate = new Date(dateValue);
        if (!Number.isNaN(parsedDate.getTime())) {
            return parsedDate.toISOString().slice(0, 10);
        }
        return String(dateValue).slice(0, 10);
    };

    const normalizeSessionTimeKey = (timeValue) => {
        if (!timeValue) return '';
        const parts = String(timeValue).split(':');
        const hours = (parts[0] || '00').padStart(2, '0');
        const minutes = (parts[1] || '00').padStart(2, '0');
        const seconds = (parts[2] || '00').padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const normalizeSessionHoursKey = (hoursValue) => {
        const numericHours = Number(hoursValue);
        if (!Number.isFinite(numericHours)) return '0.00';
        return numericHours.toFixed(2);
    };

    const buildLectureSessionKey = (session) => {
        if (!session) return '';
        return [
            normalizeSessionDateKey(session.date),
            normalizeSessionTimeKey(session.startTime),
            normalizeSessionTimeKey(session.endTime),
            normalizeSessionHoursKey(session.hours),
            String(session.classroom || '').trim().replace(/\s+/g, ' '),
        ].join('|');
    };

    const mergeSubjectsForDisplay = (staff) => {
        if (!staff || !Array.isArray(staff.subjects)) {
            return [];
        }

        const subjectMap = new Map();

        staff.subjects.forEach((subject) => {
            const subjectKey = [subject?.subjectFullName || '', subject?.subjectShortName || ''].join('|');

            if (!subjectMap.has(subjectKey)) {
                subjectMap.set(subjectKey, {
                    subjectFullName: subject?.subjectFullName || '',
                    subjectShortName: subject?.subjectShortName || '',
                    hours: 0,
                    sessions: new Map(),
                });
            }

            const mergedSubject = subjectMap.get(subjectKey);

            (subject?.sessions || []).forEach((session) => {
                const sessionKey = buildLectureSessionKey(session);
                if (!sessionKey) return;

                if (!mergedSubject.sessions.has(sessionKey)) {
                    mergedSubject.sessions.set(sessionKey, {
                        ...session,
                        batchNames: new Set(Array.isArray(session.batchNames) ? session.batchNames : []),
                        scheduleIds: new Set(Array.isArray(session.scheduleIds) ? session.scheduleIds : []),
                    });

                    mergedSubject.hours += Number(session.hours) || 0;
                    return;
                }

                const existingSession = mergedSubject.sessions.get(sessionKey);
                (session.batchNames || []).forEach((batchName) => existingSession.batchNames.add(batchName));
                (session.scheduleIds || []).forEach((scheduleId) => existingSession.scheduleIds.add(scheduleId));

                if (!existingSession.batchGroup && session.batchGroup) {
                    existingSession.batchGroup = session.batchGroup;
                }
                if (!existingSession.classroom && session.classroom) {
                    existingSession.classroom = session.classroom;
                }
            });
        });

        return Array.from(subjectMap.values()).map((subject) => ({
            subjectFullName: subject.subjectFullName,
            subjectShortName: subject.subjectShortName,
            hours: Number(subject.hours.toFixed(2)),
            sessions: Array.from(subject.sessions.values()).map((session) => ({
                ...session,
                batchNames: Array.from(session.batchNames),
                scheduleIds: Array.from(session.scheduleIds),
            })),
        }));
    };

    const selectedStaffView = selectedStaff
        ? {
            ...selectedStaff,
            subjects: mergeSubjectsForDisplay(selectedStaff),
        }
        : null;

    // Handle date changes
    const handleStartDateChange = (e) => {
        setStartDate(e.target.value);
    };

    const handleEndDateChange = (e) => {
        setEndDate(e.target.value);
    };

    // Fetch lecture hours data
    const fetchLectureHours = async () => {
        if (!startDate || !endDate) {
            return;
        }

        setLoading(true);
        setChartLoading(true);
        try {
            const response = await axiosInstance.get('/admin/lecture-hours', {
                params: { startDate, endDate },
                validateStatus: (status) => status >= 200 && status < 300,
            });
            const data = response.data || [];

            const sortedData = data
                .slice()
                .sort((a, b) => {
                    const ah = Number(a.totalHours) || 0;
                    const bh = Number(b.totalHours) || 0;
                    return bh - ah;
                });

            setLectureData(sortedData);

            const newStaffIds = new Set(sortedData.map((s) => s.staffId));
            setImageCache((prev) => {
                const updated = { ...prev };
                Object.keys(updated).forEach((key) => {
                    const staffId = parseInt(key, 10);
                    if (!newStaffIds.has(staffId)) {
                        const url = updated[staffId];
                        if (typeof url === 'string' && url.startsWith('blob:')) {
                            try {
                                URL.revokeObjectURL(url);
                            } catch {}
                        }
                        delete updated[staffId];
                    }
                });
                return updated;
            });

            if (sortedData.length > 0) {
                if (!selectedStaff || !sortedData.find((s) => s.staffId === selectedStaff.staffId)) {
                    setSelectedStaff(sortedData[0]);
                } else {
                    const updatedStaff = sortedData.find((s) => s.staffId === selectedStaff.staffId);
                    if (updatedStaff) {
                        setSelectedStaff(updatedStaff);
                    }
                }
            } else {
                setSelectedStaff(null);
            }
        } catch (error) {
            console.error('Failed to fetch lecture hours:', error);
        } finally {
            setLoading(false);
            setChartLoading(false);
        }
    };

    // Auto-fetch when dates change
    useEffect(() => {
        // Auto-fetch only on initial load with default dates
        if (startDate === defaultStart && endDate === defaultEnd) {
            fetchLectureHours();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lazy load staff images using mediaUtils
    const loadStaffImage = async (staff) => {
        const staffId = staff.staffId;
        // Skip if already loading for this staff
        if (loadingImages.has(staffId)) return;
        
        const raw = staff.imagePath && String(staff.imagePath).trim();
        console.log('[LectureHours] Loading image for:', staff.staffName, 'Path:', raw);
        
        if (!raw) {
            setImageCache(prev => ({ ...prev, [staffId]: null }));
            return;
        }

        setLoadingImages(prev => new Set(prev).add(staffId));
        
        try {
            // Normalize filename using mediaUtils
            const filename = normalizeImagePath(raw);
            console.log('[LectureHours] Normalized filename:', filename);
            
            if (!filename) {
                setImageCache(prev => ({ ...prev, [staffId]: null }));
            } else {
                // Fetch image using mediaUtils (this uses local storage)
                const imageUrl = await fetchStaffImageBlob(filename, {
                    mediaBaseUrl,
                    headerName,
                    headerValue,
                });
                
                console.log('[LectureHours] Loaded image URL:', imageUrl);
                setImageCache(prev => ({ ...prev, [staffId]: imageUrl }));
            }
        } catch (e) {
            console.error('[LectureHours] Error loading staff image:', e);
            setImageCache(prev => ({ ...prev, [staffId]: null }));
        } finally {
            setLoadingImages(prev => { const n = new Set(prev); n.delete(staffId); return n; });
        }
    };

    // Load images for all staff when data changes
    useEffect(() => {
        lectureData.forEach((staff) => {
            if (!staff.imagePath) return;
            // Avoid reloading if we already have a cached URL or currently loading
            const hasCached = imageCache[staff.staffId] !== undefined;
            const isLoading = loadingImages.has(staff.staffId);
            if (!hasCached && !isLoading) {
                loadStaffImage(staff);
            }
        });
        // Only depend on lectureData to avoid re-trigger loops on cache state updates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lectureData]);

    // Cleanup blob URLs on unmount
    useEffect(() => {
        return () => {
            Object.values(imageCache).forEach(url => {
                if (typeof url === 'string' && url.startsWith('blob:')) {
                    try { URL.revokeObjectURL(url); } catch {}
                }
            });
        };
    }, [imageCache]);

    // Handle image load error
    const handleImageError = (staffId) => {
        console.log('[LectureHours] Image load error for staffId:', staffId);
        setImageErrors(prev => new Set(prev).add(staffId));
    };

    // Only show chart loader during real data fetches, not on staff switch
    // so we intentionally do not toggle chartLoading on selectedStaff changes.

    // Helper to resolve staff image path directly from data or cache
    const getStaffImageSrc = (staff) => {
        if (!staff) return null;
        if (imageErrors.has(staff.staffId)) {
            return null;
        }
        // Prefer any cached blob URL first
        if (imageCache[staff.staffId]) {
            return imageCache[staff.staffId];
        }
        // Otherwise fall back to the raw imagePath coming from the API
        if (staff.imagePath) {
            const raw = String(staff.imagePath).trim();
            if (raw) return raw;
        }
        return null;
    };

    // Format time to 12-hour format with AM/PM
    const formatTimeTo12Hour = (time24) => {
        const [hours, minutes] = time24.split(':').map(Number);
        const period = hours >= 12 ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    };

    // Compute end time from a HH:MM start time and duration in minutes
    const computeEndFromStartAndDuration = (startTime, durationMinutes) => {
        if (!startTime || !durationMinutes) return '';
        const [hStr, mStr] = String(startTime).split(':');
        const startH = parseInt(hStr || '0', 10);
        const startM = parseInt(mStr || '0', 10);
        if (Number.isNaN(startH) || Number.isNaN(startM)) return '';
        const totalMinutes = startH * 60 + startM + durationMinutes;
        const endH = Math.floor(totalMinutes / 60) % 24;
        const endM = totalMinutes % 60;
        return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    };

    // Compute duration in minutes from HH:MM or HH:MM:SS times
    const computeDurationMinutesFromTimes = (startTime, endTime) => {
        if (!startTime || !endTime) return null;
        const parse = (t) => {
            const parts = String(t).split(':');
            const h = parseInt(parts[0] || '0', 10);
            const m = parseInt(parts[1] || '0', 10);
            if (Number.isNaN(h) || Number.isNaN(m)) return null;
            return h * 60 + m;
        };
        const s = parse(startTime);
        const e = parse(endTime);
        if (s === null || e === null) return null;
        let diff = e - s;
        if (diff <= 0) diff += 24 * 60;
        if (diff <= 0) return null;
        return diff;
    };

    // Generate colors for chart bars
    const generateColors = (count) => {
        const colors = [
            '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
            '#3b82f6', '#ef4444', '#14b8a6', '#f97316', '#84cc16',
        ];
        return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
    };

    // Prepare chart data for selected staff
    const prepareChartData = (staff) => {
        if (!staff) return [];

        return (staff.subjects || []).map((subject) => {
            const num = Number(subject && subject.hours);
            const safeHours = Number.isFinite(num) ? parseFloat(num.toFixed(2)) : 0;
            return {
                name: subject.subjectShortName || subject.subjectFullName,
                hours: safeHours,
                fullName: subject.subjectFullName,
            };
        });
    };

    const preparedChartData = prepareChartData(selectedStaffView);
    const chartData = preparedChartData
        .slice()
        .sort((a, b) => b.hours - a.hours);
    const chartColors = generateColors(chartData.length);

    // Download all staff summary Excel
    const downloadAllStaffSummaryExcel = async () => {
        try {
            const [lectureResponse, batchesResponse] = await Promise.all([
                axiosInstance.get('/admin/lecture-hours', {
                    params: { startDate, endDate },
                    validateStatus: (status) => status >= 200 && status < 300,
                }),
                axiosInstance.get('/admin/batches', {
                    validateStatus: (status) => status >= 200 && status < 300,
                }),
            ]);

            const freshLectureData = Array.isArray(lectureResponse.data) ? lectureResponse.data : [];
            const batches = Array.isArray(batchesResponse.data?.batches) ? batchesResponse.data.batches : [];
            const batchCourseMap = new Map(
                batches
                    .filter((batch) => batch?.batchInternalName)
                    .map((batch) => [
                        String(batch.batchInternalName).trim(),
                        {
                            courseFullName: batch.course?.fullName || batch.courseFullName || '',
                            courseShortName: batch.course?.shortName || batch.courseShortName || '',
                        },
                    ])
            );

            let exportRows = [];
            const summaryData = freshLectureData.map((staff) => ({
                employeeNumber: staff.employeeNumber || '',
                staffName: staff.staffName || '',
                totalHours: Number(staff.totalHours || 0),
                subjectCount: (staff.subjects || []).length,
            }));

            freshLectureData.forEach((staff) => {
                const totalHours = Number(staff.totalHours || 0);
                (staff.subjects || []).forEach((subject) => {
                    const fallbackCourses = new Map();
                    (subject.sessions || []).forEach((session) => {
                        (session.batchNames || []).forEach((batchName) => {
                            const mappedCourse = batchCourseMap.get(String(batchName || '').trim());
                            if (!mappedCourse) return;
                            const courseKey = `${mappedCourse.courseFullName}|${mappedCourse.courseShortName}`;
                            if (!fallbackCourses.has(courseKey)) {
                                fallbackCourses.set(courseKey, mappedCourse);
                            }
                        });
                    });

                    const fallbackCourseValues = Array.from(fallbackCourses.values());
                    const resolvedCourses = subject.courseFullName || subject.courseShortName
                        ? [{
                            courseFullName: subject.courseFullName || '',
                            courseShortName: subject.courseShortName || '',
                        }]
                        : (fallbackCourseValues.length > 0
                            ? fallbackCourseValues
                            : [{ courseFullName: '', courseShortName: '' }]);

                    resolvedCourses.forEach((course) => {
                        exportRows.push({
                            employeeNumber: staff.employeeNumber || '',
                            staffName: staff.staffName || '',
                            courseFullName: course.courseFullName || '',
                            courseShortName: course.courseShortName || '',
                            subjectFullName: subject.subjectFullName || '',
                            subjectShortName: subject.subjectShortName || '',
                            subjectHours: Number(subject.hours || 0),
                            totalStaffHours: totalHours,
                        });
                    });
                });
            });

            if (!exportRows.length) {
                alert('No staff data loaded. Please fetch lecture hours first.');
                return;
            }

            const subjectHeaders = ['#', 'Employee No.', 'Staff Name', 'Course', 'Course Code', 'Subject', 'Subject Code', 'Subject Hours', 'Total Staff Hours'];
            const subjectRows = exportRows.map((row, index) => ([
                index + 1,
                row.employeeNumber || '',
                row.staffName || '',
                row.courseFullName || row.courseShortName || '',
                row.courseShortName || '',
                row.subjectFullName || '',
                row.subjectShortName || '',
                Number(row.subjectHours || 0).toFixed(2),
                Number(row.totalStaffHours || 0).toFixed(2),
            ]));
            const subjectSheet = XLSX.utils.aoa_to_sheet([subjectHeaders, ...subjectRows]);
            subjectSheet['!cols'] = [
                { wch: 5 },
                { wch: 15 },
                { wch: 28 },
                { wch: 35 },
                { wch: 14 },
                { wch: 40 },
                { wch: 16 },
                { wch: 16 },
                { wch: 18 },
            ];

            const summaryHeaders = ['#', 'Employee No.', 'Staff Name', 'Total Hours', 'No. of Subjects', 'Period'];
            const summaryRows = summaryData.map((staff, index) => ([
                index + 1,
                staff.employeeNumber || '',
                staff.staffName || '',
                Number(staff.totalHours || 0).toFixed(2),
                Number(staff.subjectCount || 0),
                `${startDate} to ${endDate}`,
            ]));
            const summarySheet = XLSX.utils.aoa_to_sheet([summaryHeaders, ...summaryRows]);
            summarySheet['!cols'] = [
                { wch: 5 },
                { wch: 15 },
                { wch: 28 },
                { wch: 14 },
                { wch: 16 },
                { wch: 25 },
            ];

            const courseSummaryMap = new Map();
            exportRows.forEach((row) => {
                const courseName = row.courseFullName || row.courseShortName || 'Unknown Course';
                const courseCode = row.courseShortName || '';
                const staffName = row.staffName || '';
                const employeeNumber = row.employeeNumber || '';
                const courseKey = `${employeeNumber}|${staffName}|${courseName}|${courseCode}`;

                if (!courseSummaryMap.has(courseKey)) {
                    courseSummaryMap.set(courseKey, {
                        employeeNumber,
                        staffName,
                        courseName,
                        courseCode,
                        courseHours: 0,
                        subjectNames: new Set(),
                        totalStaffHours: Number(row.totalStaffHours || 0),
                    });
                }

                const courseEntry = courseSummaryMap.get(courseKey);
                courseEntry.courseHours += Number(row.subjectHours || 0);
                courseEntry.totalStaffHours = Number(row.totalStaffHours || 0);
                if (row.subjectFullName || row.subjectShortName) {
                    courseEntry.subjectNames.add(row.subjectFullName || row.subjectShortName);
                }
            });

            const courseSummaryRows = Array.from(courseSummaryMap.values())
                .sort((a, b) => b.courseHours - a.courseHours)
                .map((course, index) => ([
                    index + 1,
                    course.employeeNumber,
                    course.staffName,
                    course.courseName,
                    course.courseCode,
                    Number(course.courseHours || 0).toFixed(2),
                    course.subjectNames.size,
                    Array.from(course.subjectNames).join(', '),
                    Number(course.totalStaffHours || 0).toFixed(2),
                    `${startDate} to ${endDate}`,
                ]));

            const courseSummaryHeaders = ['#', 'Employee No.', 'Staff Name', 'Course', 'Course Code', 'Course Hours', 'No. of Subjects', 'Course Subjects', 'Total Staff Hours', 'Period'];
            const courseSummarySheet = XLSX.utils.aoa_to_sheet([courseSummaryHeaders, ...courseSummaryRows]);
            courseSummarySheet['!cols'] = [
                { wch: 5 },
                { wch: 15 },
                { wch: 28 },
                { wch: 40 },
                { wch: 18 },
                { wch: 14 },
                { wch: 16 },
                { wch: 55 },
                { wch: 18 },
                { wch: 25 },
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, subjectSheet, 'Subject-wise Hours');
            XLSX.utils.book_append_sheet(wb, courseSummarySheet, 'Course-wise Hours');
            XLSX.utils.book_append_sheet(wb, summarySheet, 'Staff Summary');
            XLSX.writeFile(wb, `All_Staff_Lecture_Hours_${startDate}_to_${endDate}.xlsx`);
        } catch (error) {
            console.error('Failed to download all staff lecture hours:', error);
            alert('Failed to download lecture hours. Please try again.');
        }
    };

    // Download Excel function
    const downloadExcel = () => {
        if (!selectedStaffView) {
            alert('Please select a staff member first');
            return;
        }

        // Prepare data for Excel
        const excelData = [];
        
        // Add header info
        excelData.push(['Lecture Hours Report']);
        excelData.push(['Staff Name:', selectedStaffView.staffName]);
        excelData.push(['Employee Number:', selectedStaffView.employeeNumber || 'N/A']);
        excelData.push(['Period:', `${startDate} to ${endDate}`]);
        excelData.push(['Total Hours:', selectedStaffView.totalHours.toFixed(2)]);
        excelData.push([]); // Empty row

        // Add all combined sessions (grouped by subject, batch group, date, time and classroom)
        excelData.push(['Subject', 'Subject Code', 'Date', 'Start Time', 'End Time', 'Batches', 'Group', 'Classroom', 'Hours', 'Status']);
        
        (selectedStaffView.subjects || []).forEach((subject) => {
            (subject.sessions || []).forEach((session) => {
                const batchNames = Array.isArray(session.batchNames) ? session.batchNames : [];
                const batchCell = batchNames.join(', ');
                const status = batchNames.length > 1 ? `Multi-batch (${batchNames.length})` : 'Single batch';

                excelData.push([
                    subject.subjectFullName,
                    subject.subjectShortName,
                    session.date,
                    formatTimeTo12Hour(session.startTime),
                    formatTimeTo12Hour(session.endTime),
                    batchCell,
                    session.batchGroup || '',
                    session.classroom || '',
                    session.hours.toFixed(2),
                    status
                ]);
            });
        });

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Set column widths
        ws['!cols'] = [
            { wch: 30 }, // Subject
            { wch: 15 }, // Subject Code
            { wch: 12 }, // Date
            { wch: 12 }, // Start Time
            { wch: 12 }, // End Time
            { wch: 25 }, // Batch
            { wch: 12 }, // Group
            { wch: 15 }, // Classroom
            { wch: 10 }, // Hours
            { wch: 15 }, // Status
        ];

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Lecture Hours');

        // Generate filename
        const fileName = `Lecture_Hours_${selectedStaffView.staffName.replace(/\s+/g, '_')}_${startDate}_to_${endDate}.xlsx`;

        // Download
        XLSX.writeFile(wb, fileName);
    };

    return (
        <DashboardLayout title="Lecture Hours">
            <div className="min-h-screen p-6">
                <div className="mx-auto max-w-7xl">
                    {/* Page Header */}
                    <div className="mb-6 rounded-xl bg-white p-6 shadow-lg border border-gray-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-indigo-600">
                                        <path d="M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z" />
                                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z" clipRule="evenodd" />
                                    </svg>
                                    Lecture Hours
                                </h1>
                                <p className="mt-2 text-sm text-gray-600">
                                    View and analyze lecture hours for the selected period
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Date Range Filter */}
                    <div className="mb-6 rounded-xl bg-white p-6 shadow-lg border border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Start Date */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                        <path d="M5.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H6a.75.75 0 0 1-.75-.75V12ZM6 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H6ZM7.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H8a.75.75 0 0 1-.75-.75V12ZM8 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H8ZM9.25 10a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H10a.75.75 0 0 1-.75-.75V10ZM10 11.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V12a.75.75 0 0 0-.75-.75H10ZM9.25 14a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H10a.75.75 0 0 1-.75-.75V14ZM12 9.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V10a.75.75 0 0 0-.75-.75H12ZM11.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H12a.75.75 0 0 1-.75-.75V12ZM12 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H12ZM13.25 10a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H14a.75.75 0 0 1-.75-.75V10ZM14 11.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V12a.75.75 0 0 0-.75-.75H14Z" />
                                        <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                    </svg>
                                    Begin Date
                                </label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={handleStartDateChange}
                                    className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-sm font-medium focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/20 transition-all"
                                />
                                <p className="mt-2 text-xs text-gray-500">
                                    Start of the period (Default: {new Date(defaultStart).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                                </p>
                            </div>

                            {/* End Date */}
                            <div>
                                <label className="mb-2 block text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-purple-600">
                                        <path d="M5.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H6a.75.75 0 0 1-.75-.75V12ZM6 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H6ZM7.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H8a.75.75 0 0 1-.75-.75V12ZM8 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H8ZM9.25 10a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H10a.75.75 0 0 1-.75-.75V10ZM10 11.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V12a.75.75 0 0 0-.75-.75H10ZM9.25 14a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H10a.75.75 0 0 1-.75-.75V14ZM12 9.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V10a.75.75 0 0 0-.75-.75H12ZM11.25 12a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H12a.75.75 0 0 1-.75-.75V12ZM12 13.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V14a.75.75 0 0 0-.75-.75H12ZM13.25 10a.75.75 0 0 1 .75-.75h.01a.75.75 0 0 1 .75.75v.01a.75.75 0 0 1-.75.75H14a.75.75 0 0 1-.75-.75V10ZM14 11.25a.75.75 0 0 0-.75.75v.01c0 .414.336.75.75.75h.01a.75.75 0 0 0 .75-.75V12a.75.75 0 0 0-.75-.75H14Z" />
                                        <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                    </svg>
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={handleEndDateChange}
                                    className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-sm font-medium focus:border-purple-500 focus:outline-none focus:ring-4 focus:ring-purple-500/20 transition-all"
                                />
                                <p className="mt-2 text-xs text-gray-500">
                                    End of the period (Default: {new Date(defaultEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })})
                                </p>
                            </div>
                        </div>

                        {/* Selected Period Info and Fetch Button */}
                        <div className="mt-6 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
                            <div className="flex-1 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 p-4 border border-indigo-100">
                                <div className="flex items-center gap-2 text-sm">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
                                    </svg>
                                    <span className="font-semibold text-gray-700">Selected Period:</span>
                                    <span className="text-gray-900 font-medium">
                                        {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                        {' '}-{' '}
                                        {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                    <span className="ml-2 inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-medium text-indigo-800">
                                        {Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
                                    </span>
                                </div>
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex gap-3 flex-wrap">
                                {/* Download All Staff Summary Button */}
                                <button
                                    onClick={downloadAllStaffSummaryExcel}
                                    disabled={!lectureData.length}
                                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-teal-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    title="Download all staff members list with lecture hours summary"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                        <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
                                        <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
                                    </svg>
                                    <span>Download All Staff</span>
                                </button>

                                {/* Download Excel Button */}
                                <button
                                    onClick={downloadExcel}
                                    disabled={!selectedStaffView}
                                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                    title="Download Excel Report for selected staff"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                        <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
                                        <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
                                    </svg>
                                    <span>Download Excel</span>
                                </button>

                                {/* Fetch Button */}
                                <button
                                    onClick={fetchLectureHours}
                                    disabled={loading || !startDate || !endDate}
                                    className="flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                <path fillRule="evenodd" d="M15.312 11.424a5.5 5.5 0 0 1-9.201 2.466l-.312-.311h2.433a.75.75 0 0 0 0-1.5H3.989a.75.75 0 0 0-.75.75v4.242a.75.75 0 0 0 1.5 0v-2.43l.31.31a7 7 0 0 0 11.712-3.138.75.75 0 0 0-1.449-.39Zm1.23-3.723a.75.75 0 0 0 .219-.53V2.929a.75.75 0 0 0-1.5 0V5.36l-.31-.31A7 7 0 0 0 3.239 8.188a.75.75 0 1 0 1.448.389A5.5 5.5 0 0 1 13.89 6.11l.311.31h-2.432a.75.75 0 0 0 0 1.5h4.243a.75.75 0 0 0 .53-.219Z" clipRule="evenodd" />
                                            </svg>
                                            <span>Fetch Lecture Hours</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Content Area - Lecture hours data */}
                    {lectureData.length === 0 && !loading ? (
                        <div className="rounded-xl bg-white p-8 shadow-lg border border-gray-100">
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-indigo-600">
                                        <path d="M11.25 4.533A9.707 9.707 0 0 0 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No Lecture Hours Data
                                </h3>
                                <p className="text-sm text-gray-600 max-w-md">
                                    No lecture hours found for the selected period. 
                                    Please try a different date range.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Staff Cards Grid */}
                            <div className="rounded-xl bg-white p-6 shadow-lg border border-gray-100">
                                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-indigo-600">
                                        <path d="M4.5 6.375a4.125 4.125 0 1 1 8.25 0 4.125 4.125 0 0 1-8.25 0ZM14.25 8.625a3.375 3.375 0 1 1 6.75 0 3.375 3.375 0 0 1-6.75 0ZM1.5 19.125a7.125 7.125 0 0 1 14.25 0v.003l-.001.119a.75.75 0 0 1-.363.63 13.067 13.067 0 0 1-6.761 1.873c-2.472 0-4.786-.684-6.76-1.873a.75.75 0 0 1-.364-.63l-.001-.122ZM17.25 19.128l-.001.144a2.25 2.25 0 0 1-.233.96 10.088 10.088 0 0 0 5.06-1.01.75.75 0 0 0 .42-.643 4.875 4.875 0 0 0-6.957-4.611 8.586 8.586 0 0 1 1.71 5.157v.003Z" />
                                    </svg>
                                    Staff Members ({lectureData.length})
                                </h2>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {lectureData.map((staff, idx) => (
                                        <div
                                            key={staff.staffId}
                                            onClick={() => setSelectedStaff(staff)}
                                            className={`relative cursor-pointer rounded-lg border-2 p-4 transition-all hover:shadow-md ${
                                                selectedStaff?.staffId === staff.staffId
                                                    ? 'border-indigo-500 bg-indigo-50'
                                                    : 'border-gray-200 bg-white hover:border-indigo-300'
                                            }`}
                                        >
                                            {/* Order Badge */}
                                            <div className="absolute top-2 right-2">
                                                <span
                                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shadow-sm ${
                                                        selectedStaff?.staffId === staff.staffId
                                                            ? 'bg-indigo-600 text-white'
                                                            : 'bg-gray-200 text-gray-700'
                                                    }`}
                                                    title={`#${idx + 1}`}
                                                >
                                                    {idx + 1}
                                                </span>
                                            </div>
                                            <div className="flex items-start gap-3">
                                                {/* Avatar */}
                                                <div className="flex-shrink-0">
                                                   <img
                                                            src={staff.imagePath != null ? getImageURL(staff.imagePath) : "https://placehold.co/100x100/png?text=No+Image"}
                                                            alt={staff.staffName}
                                                            className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                                                            onError={() => handleImageError(staff.staffId)}
                                                        />
                                                </div>
                                                
                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-gray-900 truncate text-sm">
                                                        {staff.staffName}
                                                    </h3>
                                                    {staff.employeeNumber && (
                                                        <p className="text-xs text-gray-500 truncate">
                                                            ID: {staff.employeeNumber}
                                                        </p>
                                                    )}
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <span className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                                                            {staff.totalHours.toFixed(1)} hrs
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            {staff.subjects.length} subject{staff.subjects.length !== 1 ? 's' : ''}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Selected Staff Details */}
                            {selectedStaffView && (
                                <>
                                    {/* Staff Header Card */}
                                    <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-purple-700 p-6 shadow-lg text-white">
                                                <div className="flex items-center gap-4">
                                            {getStaffImageSrc(selectedStaffView) && !imageErrors.has(selectedStaffView.staffId) ? (
                                                <img
                                                    src={getStaffImageSrc(selectedStaffView)}
                                                    alt={selectedStaffView.staffName}
                                                    className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
                                                    onError={() => handleImageError(selectedStaffView.staffId)}
                                                />
                                            ) : (
                                                <div className="w-20 h-20 rounded-full bg-indigo-100 border-4 border-white shadow-lg flex items-center justify-center text-indigo-700 font-semibold text-xl">
                                                    {(selectedStaffView.staffName || '?')
                                                        .toString()
                                                        .split(/\s+/)
                                                        .map((part) => part[0])
                                                        .join('')
                                                        .slice(0, 2)
                                                        .toUpperCase()}
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <h2 className="text-2xl font-bold">{selectedStaffView.staffName}</h2>
                                                {selectedStaffView.employeeNumber && (
                                                    <p className="text-indigo-100 mt-1">
                                                        Employee ID: {selectedStaffView.employeeNumber}
                                                    </p>
                                                )}
                                                <div className="mt-3 flex items-center gap-4">
                                                    <div className="flex items-center gap-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="text-lg font-semibold">
                                                            Total: {selectedStaffView.totalHours.toFixed(1)} hours
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                            <path d="M10.75 16.82A7.462 7.462 0 0 1 15 15.5c.71 0 1.396.098 2.046.282A.75.75 0 0 0 18 15.06v-11a.75.75 0 0 0-.546-.721A9.006 9.006 0 0 0 15 3a8.963 8.963 0 0 0-4.25 1.065V16.82ZM9.25 4.065A8.963 8.963 0 0 0 5 3c-.85 0-1.673.118-2.454.339A.75.75 0 0 0 2 4.06v11a.75.75 0 0 0 .954.721A7.506 7.506 0 0 1 5 15.5c1.579 0 3.042.487 4.25 1.32V4.065Z" />
                                                        </svg>
                                                        <span>{selectedStaffView.subjects.length} Subjects</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Subject Summary Table */}
                                    <div className="rounded-xl bg-white p-6 shadow-lg border border-gray-100">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-indigo-600">
                                                    <path d="M11.25 4.533A9.707 9.707 0 0 0 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
                                                </svg>
                                                Subject Summary
                                            </h3>
                                            
                                            {/* View Mode Toggle */}
                                            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                                <button
                                                    onClick={() => setViewMode('bySubject')}
                                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                                        viewMode === 'bySubject'
                                                            ? 'bg-white text-indigo-600 shadow-sm'
                                                            : 'text-gray-600 hover:text-gray-900'
                                                    }`}
                                                >
                                                    By Subject
                                                </button>
                                                <button
                                                    onClick={() => setViewMode('allCombined')}
                                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                                        viewMode === 'allCombined'
                                                            ? 'bg-white text-indigo-600 shadow-sm'
                                                            : 'text-gray-600 hover:text-gray-900'
                                                    }`}
                                                >
                                                    All Combined
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {viewMode === 'bySubject' ? (
                                            /* By Subject View */
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Subject
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Hours
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Sessions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(selectedStaffView.subjects || [])
                                                            .slice()
                                                            .sort((a, b) => {
                                                                const ah = Number(a.hours) || 0;
                                                                const bh = Number(b.hours) || 0;
                                                                return bh - ah;
                                                            })
                                                            .map((subject, idx) => (
                                                            <tr key={idx} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <div className="text-sm font-medium text-gray-900">
                                                                        {subject.subjectFullName}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500">
                                                                        {subject.subjectShortName}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                                                        {subject.hours.toFixed(2)} hrs
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="text-sm text-gray-900">
                                                                            {subject.sessions.length} session{subject.sessions.length !== 1 ? 's' : ''}
                                                                        </div>
                                                                        <button
                                                                            onClick={() => {
                                                                                setModalSubject(subject);
                                                                                setShowModal(true);
                                                                            }}
                                                                            className="ml-4 inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                                                                                <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd" />
                                                                            </svg>
                                                                            View Details
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                        <tr className="bg-indigo-50 font-semibold">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                Total
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-indigo-600 text-white">
                                                                    {selectedStaffView.totalHours.toFixed(2)} hrs
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4"></td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        ) : (
                                            /* All Combined View */
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Subject
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Date
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Time
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Batch
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Classroom
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Hours
                                                            </th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {(selectedStaffView.subjects || []).flatMap((subject, subjectIdx) =>
                                                            (subject.sessions || []).map((session, sessionIdx) => (
                                                                <tr key={`${subjectIdx}-${sessionIdx}`} className="hover:bg-gray-50">
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        <div className="text-sm font-medium text-gray-900">
                                                                            {subject.subjectFullName}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {subject.subjectShortName}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                        {session.date
                                                                            ? new Date(session.date).toLocaleDateString('en-US', {
                                                                                  year: 'numeric',
                                                                                  month: 'short',
                                                                                  day: 'numeric',
                                                                              })
                                                                            : ''}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                        {formatTimeTo12Hour(session.startTime)} - {formatTimeTo12Hour(session.endTime)}
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="text-sm text-gray-900">
                                                                            {Array.isArray(session.batchNames) && session.batchNames.length > 0
                                                                                ? session.batchNames.join(', ')
                                                                                : ''}
                                                                            {session.batchGroup && (
                                                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                                                    {session.batchGroup}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                        {session.classroom}
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                            {session.hours.toFixed(2)} hrs
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                const startHHMM = (session.startTime || '').slice(0, 5);
                                                                                const endHHMM = (session.endTime || '').slice(0, 5);
                                                                                const inferredDuration = computeDurationMinutesFromTimes(session.startTime, session.endTime);
                                                                                setEditSessionContext({
                                                                                    subject,
                                                                                    session,
                                                                                    staff: selectedStaff,
                                                                                });
                                                                                setEditTime({ start: startHHMM, end: endHHMM });
                                                                                setEditDurationMin(inferredDuration);
                                                                                setShowEditModal(true);
                                                                            }}
                                                                            className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 border border-indigo-100 transition-colors"
                                                                        >
                                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                                                                                <path d="M5.433 13.917 4.5 15.5l1.583-.933 9.2-9.2a1.125 1.125 0 1 0-1.59-1.59l-9.26 9.26a1 1 0 0 0-.243.38l-.757 2.271a.375.375 0 0 0 .474.474l2.27-.757a1 1 0 0 0 .38-.243l9.26-9.26a1.125 1.125 0 1 0-1.59-1.59l-9.2 9.2Z" />
                                                                            </svg>
                                                                            Edit
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                        <tr className="bg-indigo-50 font-semibold">
                                                            <td colSpan={6} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                Total
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-indigo-600 text-white">
                                                                    {selectedStaffView.totalHours.toFixed(2)} hrs
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>

                                    {/* Bar Chart */}
                                    <div className="rounded-xl bg-white p-6 shadow-lg border border-gray-100">
                                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-indigo-600">
                                                <path fillRule="evenodd" d="M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm4.5 7.5a.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-1.5 0v-2.25a.75.75 0 0 1 .75-.75Zm3.75-1.5a.75.75 0 0 0-1.5 0v4.5a.75.75 0 0 0 1.5 0V12Zm2.25-3a.75.75 0 0 1 .75.75v6.75a.75.75 0 0 1-1.5 0V9.75A.75.75 0 0 1 13.5 9Zm3.75-1.5a.75.75 0 0 0-1.5 0v9a.75.75 0 0 0 1.5 0v-9Z" clipRule="evenodd" />
                                            </svg>
                                            Hours by Subject
                                        </h3>
                                        
                                        {chartLoading ? (
                                            <div className="flex flex-col items-center justify-center h-[400px]">
                                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mb-4"></div>
                                                <p className="text-gray-600 font-medium">Loading chart...</p>
                                            </div>
                                        ) : chartData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height={400}>
                                                <BarChart
                                                    data={chartData}
                                                    layout="vertical"
                                                    margin={{ top: 20, right: 40, left: 120, bottom: 20 }}
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                                    <XAxis 
                                                        type="number"
                                                        allowDecimals
                                                        tick={{ fontSize: 12 }}
                                                        label={{ value: 'Hours', position: 'insideBottomRight', offset: -10 }}
                                                    />
                                                    <YAxis 
                                                        type="category"
                                                        dataKey="name"
                                                        width={100}
                                                        tick={{ fontSize: 12, width: 100, dx: -4 }}
                                                    />
                                                    <Tooltip 
                                                        cursor={{ fill: 'rgba(99, 102, 241, 0.06)' }}
                                                        wrapperStyle={{ outline: 'none', zIndex: 10 }}
                                                        contentStyle={{
                                                            backgroundColor: 'white',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '0.75rem',
                                                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                                            padding: '12px 14px'
                                                        }}
                                                        labelStyle={{
                                                            fontWeight: '600',
                                                            color: '#111827',
                                                            marginBottom: '4px'
                                                        }}
                                                        itemStyle={{
                                                            color: '#6366f1',
                                                            fontWeight: '500',
                                                            padding: 0
                                                        }}
                                                        formatter={(value) => {
                                                            const v = Number(value).toFixed(2);
                                                            return [`${v} hours`, 'Lecture Hours'];
                                                        }}
                                                        labelFormatter={(label) => {
                                                            const item = chartData.find((d) => d.name === label);
                                                            return item?.fullName || label;
                                                        }}
                                                    />
                                                    <Legend />
                                                    <Bar 
                                                        dataKey="hours" 
                                                        name="Lecture Hours"
                                                        radius={[0, 8, 8, 0]}
                                                        minPointSize={2}
                                                        barSize={24}
                                                        isAnimationActive={false}
                                                        fillOpacity={0.95}
                                                    >
                                                        <LabelList 
                                                            dataKey="hours" 
                                                            position="right" 
                                                            formatter={(v) => `${Number(v).toFixed(2)}`}
                                                            fill="#111827" 
                                                            fontSize={12} 
                                                        />
                                                        {chartData.map((entry, index) => (
                                                            <Cell 
                                                                key={`cell-${index}`} 
                                                                fill={chartColors[index]} 
                                                            />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="flex items-center justify-center h-64 text-gray-500">
                                                No data available for chart
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Details Modal */}
                    {showModal && modalSubject && (
                        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setShowModal(false)}>
                            <div className="flex min-h-screen items-center justify-center p-4">
                                {/* Backdrop */}
                                <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
                                
                                {/* Modal */}
                                <div 
                                    className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Header */}
                                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold text-white">
                                                {modalSubject.subjectFullName}
                                            </h3>
                                            <p className="text-indigo-100 text-sm mt-1">
                                                {modalSubject.subjectShortName} • {selectedStaffView?.staffName}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setShowModal(false)}
                                            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6">
                                                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Summary Stats */}
                                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4 border-b border-gray-200">
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-indigo-600">
                                                    {modalSubject.hours.toFixed(2)}
                                                </div>
                                                <div className="text-sm text-gray-600">Total Hours</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-purple-600">
                                                    {modalSubject.sessions.length}
                                                </div>
                                                <div className="text-sm text-gray-600">Sessions</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-pink-600">
                                                    {(modalSubject.hours / modalSubject.sessions.length).toFixed(2)}
                                                </div>
                                                <div className="text-sm text-gray-600">Avg Hours/Session</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sessions List */}
                                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600">
                                                <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                            </svg>
                                            Session Details
                                        </h4>
                                        
                                        <div className="space-y-3">
                                            {(modalSubject.sessions || []).map((session, idx) => (
                                                <div 
                                                    key={idx}
                                                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                                                >
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        {/* Left Column */}
                                                        <div className="space-y-2">
                                                            {/* Session Number & Date */}
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                                                                    <span className="text-indigo-700 font-bold text-xs">
                                                                        #{idx + 1}
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                                                                        <path fillRule="evenodd" d="M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z" clipRule="evenodd" />
                                                                    </svg>
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        {new Date(session.date).toLocaleDateString('en-US', { 
                                                                            weekday: 'short',
                                                                            year: 'numeric',
                                                                            month: 'short', 
                                                                            day: 'numeric' 
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Time */}
                                                            <div className="flex items-center gap-2 ml-11">
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                                                                </svg>
                                                                <span className="text-sm text-gray-700 font-medium">
                                                                    {formatTimeTo12Hour(session.startTime)} - {formatTimeTo12Hour(session.endTime)}
                                                                </span>
                                                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-indigo-100 text-indigo-700">
                                                                    {session.hours.toFixed(2)} hrs
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Right Column */}
                                                        <div className="space-y-2">
                                                            {/* Batches */}
                                                            {Array.isArray(session.batchNames) && session.batchNames.length > 0 && (
                                                                <div className="flex items-start gap-2">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400 mt-0.5">
                                                                        <path d="M10 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM6 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM1.49 15.326a.78.78 0 0 1-.358-.442 3 3 0 0 1 4.308-3.516 6.484 6.484 0 0 0-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 0 1-2.07-.655ZM16.44 15.98a4.97 4.97 0 0 0 2.07-.654.78.78 0 0 0 .357-.442 3 3 0 0 0-4.308-3.517 6.484 6.484 0 0 1 1.907 3.96 2.32 2.32 0 0 1-.026.654ZM18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM5.304 16.19a.844.844 0 0 1-.277-.71 5 5 0 0 1 9.947 0 .843.843 0 0 1-.277.71A6.975 6.975 0 0 1 10 18a6.974 6.974 0 0 1-4.696-1.81Z" />
                                                                    </svg>
                                                                    <div className="flex-1">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-sm font-medium text-gray-900">
                                                                                Batches:
                                                                            </span>
                                                                            <span className="text-sm text-gray-700">
                                                                                {session.batchNames.join(', ')}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Batch Group */}
                                                            {session.batchGroup && (
                                                                <div className="flex items-center gap-2">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                                                                        <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
                                                                    </svg>
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        Group:
                                                                    </span>
                                                                    <span className="text-sm text-purple-700 font-medium">
                                                                        {session.batchGroup}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            
                                                            {/* Classroom */}
                                                            {session.classroom && (
                                                                <div className="flex items-center gap-2">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                                                                        <path fillRule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 0 0 .281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 1 0 3 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 0 0 2.273 1.765 11.842 11.842 0 0 0 .976.544l.062.029.018.008.006.003ZM10 11.25a2.25 2.25 0 1 0 0-4.5 2.25 2.25 0 0 0 0 4.5Z" clipRule="evenodd" />
                                                                    </svg>
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        Room:
                                                                    </span>
                                                                    <span className="text-sm text-gray-700">
                                                                        {session.classroom}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                                        <button
                                            onClick={() => setShowModal(false)}
                                            className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Edit Session Time Modal */}
                    {showEditModal && editSessionContext && (
                        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => !savingEdit && setShowEditModal(false)}>
                            <div className="flex min-h-screen items-center justify-center p-4">
                                {/* Backdrop */}
                                <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>

                                {/* Modal */}
                                <div
                                    className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Header */}
                                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
                                        <div>
                                            <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                                <span>Edit Session Time</span>
                                            </h3>
                                            <p className="text-xs text-indigo-100 mt-1">
                                                {editSessionContext.subject.subjectFullName} ({editSessionContext.subject.subjectShortName}) • {editSessionContext.staff?.staffName}
                                            </p>
                                            {editSessionContext.session?.date && (
                                                <p className="text-xs text-indigo-100 mt-0.5">
                                                    {new Date(editSessionContext.session.date).toLocaleDateString('en-US', {
                                                        weekday: 'short',
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                    })}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            onClick={() => !savingEdit && setShowEditModal(false)}
                                            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors disabled:opacity-50"
                                            disabled={savingEdit}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                                                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Body */}
                                    <div className="p-6 space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">Start Time</label>
                                                <input
                                                    type="time"
                                                    value={editTime.start}
                                                    onChange={(e) => {
                                                        const v = e.target.value;
                                                        setEditTime((t) => ({ ...t, start: v, end: computeEndFromStartAndDuration(v, editDurationMin) }));
                                                    }}
                                                    className="w-full rounded-md border-2 border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">Duration</label>
                                                <select
                                                    value={editDurationMin ?? ''}
                                                    onChange={(e) => {
                                                        const minutes = e.target.value ? parseInt(e.target.value, 10) : null;
                                                        setEditDurationMin(minutes);
                                                        setEditTime((t) => ({ ...t, end: computeEndFromStartAndDuration(t.start, minutes) }));
                                                    }}
                                                    disabled={!editTime.start}
                                                    className="w-full rounded-md border-2 border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 disabled:bg-gray-100 disabled:text-gray-400"
                                                >
                                                    <option value="">Select duration…</option>
                                                    <option value="60">1 hr</option>
                                                    <option value="90">1 hr 30 min</option>
                                                    <option value="120">2 hr</option>
                                                    <option value="150">2 hr 30 min</option>
                                                    <option value="180">3 hr</option>
                                                    <option value="210">3 hr 30 min</option>
                                                    <option value="240">4 hr</option>
                                                    <option value="270">4 hr 30 min</option>
                                                    <option value="300">5 hr</option>
                                                    <option value="330">5 hr 30 min</option>
                                                    <option value="360">6 hr</option>
                                                    <option value="390">6 hr 30 min</option>
                                                    <option value="420">7 hr</option>
                                                    <option value="450">7 hr 30 min</option>
                                                    <option value="480">8 hr</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-gray-700 mb-1">End Time</label>
                                                <input
                                                    type="time"
                                                    value={editTime.end}
                                                    readOnly
                                                    className="w-full rounded-md border-2 border-gray-300 px-3 py-2 text-sm bg-gray-100 text-gray-600"
                                                    title="End time is calculated from start time and duration"
                                                />
                                            </div>
                                        </div>

                                        <p className="text-xs text-gray-500">
                                            This will update the underlying timetable entries for all batches in this grouped session.
                                        </p>
                                    </div>

                                    {/* Footer */}
                                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                                        <button
                                            type="button"
                                            onClick={() => !savingEdit && setShowEditModal(false)}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                                            disabled={savingEdit}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (!editSessionContext || !selectedStaff) return;
                                                if (!editTime.start || !editTime.end) {
                                                    alert('Please select both start time and duration.');
                                                    return;
                                                }

                                                const normalizeToSeconds = (t) => {
                                                    if (!t) return null;
                                                    const parts = String(t).split(':');
                                                    const h = parts[0] || '00';
                                                    const m = parts[1] || '00';
                                                    const s = parts[2] || '00';
                                                    return `${h.padStart(2, '0')}:${m.padStart(2, '0')}:${s.padStart(2, '0')}`;
                                                };

                                                const oldStart = normalizeToSeconds(editSessionContext.session.startTime);
                                                const oldEnd = normalizeToSeconds(editSessionContext.session.endTime);
                                                const newStart = normalizeToSeconds(editTime.start);
                                                const newEnd = normalizeToSeconds(editTime.end);

                                                setSavingEdit(true);
                                                try {
                                                    await axiosInstance.put('/admin/lecture-hours/update-session-time', {
                                                        staffId: selectedStaff.staffId,
                                                        date: editSessionContext.session.date,
                                                        subjectShortName: editSessionContext.subject.subjectShortName,
                                                        subjectFullName: editSessionContext.subject.subjectFullName,
                                                        classroomName: editSessionContext.session.classroom,
                                                        batchGroupName: editSessionContext.session.batchGroup,
                                                        oldStartTime: oldStart,
                                                        oldEndTime: oldEnd,
                                                        newStartTime: newStart,
                                                        newEndTime: newEnd,
                                                        scheduleIds: editSessionContext.session.scheduleIds || [],
                                                    });

                                                    await fetchLectureHours();
                                                    setShowEditModal(false);
                                                    setEditSessionContext(null);
                                                } catch (error) {
                                                    console.error('Failed to update session time:', error);
                                                    alert('Failed to update session time. Please try again.');
                                                } finally {
                                                    setSavingEdit(false);
                                                }
                                            }}
                                            className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-md hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60"
                                            disabled={savingEdit}
                                        >
                                            {savingEdit ? 'Saving…' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
}

